import { createReducer } from '@reduxjs/toolkit';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash-es';

import * as Actions from './actions';
import type { PartDefinition, Part } from './types';

export interface State {
  entities: { [key: string]: Part };
  definitions: { [key: string]: PartDefinition };
  editDialogData: PartDefinition | null;
  isDefinitionsLoadedByTask: { [task: string]: boolean };
}

export const initialState: State = {
  entities: {},
  definitions: {},
  editDialogData: null,
  isDefinitionsLoadedByTask: {},
};

type LoadPartDefinitionsSuccessAction = PayloadAction<
  { definitions: PartDefinition[] },
  typeof Actions.LOAD_PART_DEFINITIONS_SUCCESS
>;
type OpenPartDefinitionEditDialogAction = PayloadAction<
  { definition: PartDefinition },
  typeof Actions.OPEN_PART_DEFINITIONS_EDIT_DIALOG
>;
type DefinitionsLoadedForTaskAction = PayloadAction<
  { task: string },
  typeof Actions.PART_DEFINITIONS_LOADED_FOR_TASK
>;
type LoadPartsSuccessAction = PayloadAction<
  { parts: Part[] },
  typeof Actions.LOAD_PARTS_SUCCESS
>;
type SavePartSuccessAction = PayloadAction<
  { part: Part },
  typeof Actions.PART_SAVE_SUCCESS
>;
type DeletePartSuccessAction = PayloadAction<
  { part: Part },
  typeof Actions.PART_DELETE_SUCCESS
>;
type PartDefinitionDeleteSuccessAction = PayloadAction<
  { definition: PartDefinition },
  typeof Actions.PART_DEFINITION_DELETE_SUCCESS
>;

const loadPartDefinitionsSuccess = (
  state: Draft<State>,
  action: LoadPartDefinitionsSuccessAction
) => {
  const { definitions } = action.payload;
  state.definitions = {
    ...state.definitions,
    ..._.keyBy(definitions, 'id'),
    ..._.keyBy(_.flatMap(definitions, 'children'), 'id'),
  };
};

const openPartDefinitionEditDialog = (
  state: Draft<State>,
  action: OpenPartDefinitionEditDialogAction
) => {
  state.editDialogData = action.payload.definition;
};

const definitionsLoadedForTask = (
  state: Draft<State>,
  action: DefinitionsLoadedForTaskAction
) => {
  const { task } = action.payload;
  state.isDefinitionsLoadedByTask[task] = true;
};

const closePartDefinitionEditDialog = (state: Draft<State>) => {
  state.editDialogData = null;
};

const loadPartsSuccess = (state: Draft<State>, action: LoadPartsSuccessAction) => {
  state.entities = { ...state.entities, ..._.keyBy(action.payload.parts, 'id') };
};

const savePartSuccess = (state: Draft<State>, action: SavePartSuccessAction) => {
  state.entities[action.payload.part.id] = action.payload.part;
};

const deletePartSuccess = (state: Draft<State>, action: DeletePartSuccessAction) => {
  // oxlint-disable-next-line no-dynamic-delete
  delete state.entities[action.payload.part.id];
};

const partDefinitionDeleteSuccess = (
  state: Draft<State>,
  action: PartDefinitionDeleteSuccessAction
) => {
  const { definition } = action.payload;
  const makeCustomParts = (d: PartDefinition) =>
    _.values(state.entities)
      .filter((part) => part.definition && part.definition === d.id)
      .forEach((part) => {
        state.entities[part.id].definition = null;
      });
  if (definition.children) {
    definition.children.forEach((child) => {
      makeCustomParts(child);
      // oxlint-disable-next-line no-dynamic-delete
      delete state.definitions[child.id];
    });
  }
  makeCustomParts(definition);
  // oxlint-disable-next-line no-dynamic-delete
  delete state.definitions[definition.id];
};

export const reducer = createReducer(initialState, (builder) => {
  builder.addCase(Actions.LOAD_PART_DEFINITIONS_SUCCESS, loadPartDefinitionsSuccess);
  builder.addCase(
    Actions.OPEN_PART_DEFINITIONS_EDIT_DIALOG,
    openPartDefinitionEditDialog
  );
  builder.addCase(
    Actions.CLOSE_PART_DEFINITION_EDIT_DIALOG,
    closePartDefinitionEditDialog
  );
  builder.addCase(Actions.PART_DEFINITIONS_LOADED_FOR_TASK, definitionsLoadedForTask);
  builder.addCase(Actions.LOAD_PARTS_SUCCESS, loadPartsSuccess);
  builder.addCase(Actions.PART_SAVE_SUCCESS, savePartSuccess);
  builder.addCase(Actions.PART_DELETE_SUCCESS, deletePartSuccess);
  builder.addCase(Actions.PART_DEFINITION_DELETE_SUCCESS, partDefinitionDeleteSuccess);
});
